import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone, MapPin, HelpCircle, Home, ChevronRight, CloudLightning, ClipboardCheck, Briefcase, MessageCircle, Cookie, Shield, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

// Breadcrumb component for interior pages
const Breadcrumbs = () => {
  const location = useLocation();
  
  // Don't show breadcrumbs on homepage
  if (location.pathname === "/") return null;
  
  // Map paths to readable names
  const pathNames = {
    "service-areas": "Service Areas",
    "insurance-claims": "Insurance Claims",
    "insurance-agents": "Insurance Agents",
    "hail-map": "Hail Map",
    "roof-assessment": "Roof Quiz",
    "reviews": "Reviews",
    "faqs": "FAQs",
    "blog": "Blog",
    "resources": "Resources",
    "guides": "Free Guides",
    "roof-replacement": "Roof Replacement",
    "storm-damage-restoration": "Storm Damage",
    "state-farm-roof-claims": "State Farm Claims",
    "american-family-roof-claims": "American Family Claims",
    "allstate-roof-claims": "Allstate Claims",
    "farmers-roof-claims": "Farmers Claims",
    "progressive-roof-claims": "Progressive Claims",
    "liberty-mutual-roof-claims": "Liberty Mutual Claims",
    "travelers-roof-claims": "Travelers Claims",
    "usaa-roof-claims": "USAA Claims",
    "nationwide-roof-claims": "Nationwide Claims",
    "auto-owners-roof-claims": "Auto-Owners Claims",
    "western-national-roof-claims": "Western National Claims",
    "farm-bureau-roof-claims": "Farm Bureau Claims",
    "safeco-roof-claims": "Safeco Claims",
    "hartford-roof-claims": "Hartford Claims",
    "chubb-roof-claims": "Chubb Claims",
  };
  
  const pathSegments = location.pathname.split("/").filter(Boolean);
  
  // Don't show for admin pages
  if (pathSegments[0] === "admin") return null;
  
  return (
    <nav 
      aria-label="Breadcrumb" 
      className="bg-gray-100 border-b border-gray-200"
      data-testid="breadcrumbs"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <Link 
              to="/" 
              className="text-gray-500 hover:text-orange transition-colors flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Link>
          </li>
          {pathSegments.map((segment, index) => {
            const path = "/" + pathSegments.slice(0, index + 1).join("/");
            const isLast = index === pathSegments.length - 1;
            const name = pathNames[segment] || segment.split("-").map(w => 
              w.charAt(0).toUpperCase() + w.slice(1)
            ).join(" ");
            
            return (
              <li key={path} className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-gray-400" />
                {isLast ? (
                  <span className="text-navy font-medium truncate max-w-[200px]">
                    {name}
                  </span>
                ) : (
                  <Link 
                    to={path}
                    className="text-gray-500 hover:text-orange transition-colors truncate max-w-[150px]"
                  >
                    {name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

// Floating CTA button that appears on scroll
const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 400px
      setIsVisible(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  return (
    <div 
      className={`fixed bottom-6 right-6 z-40 transition-all duration-300 ${
        isVisible 
          ? "translate-y-0 opacity-100" 
          : "translate-y-20 opacity-0 pointer-events-none"
      }`}
      data-testid="floating-cta"
    >
      <a href="tel:+1-555-STORM-MN">
        <Button 
          className="bg-orange hover:bg-orange-hover text-white rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
          title="Call StormCare"
        >
          <Phone className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </Button>
      </a>
      <div className="absolute -top-10 right-0 bg-navy text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
        Call StormCare
      </div>
    </div>
  );
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: "/", label: "Home", icon: Home },
    { href: "/service-areas", label: "Areas", icon: MapPin },
    { href: "/insurance-claims", label: "Insurance", icon: Briefcase },
    { href: "/hail-map", label: "Hail Map", icon: CloudLightning },
    { href: "/roof-assessment", label: "Roof Quiz", icon: ClipboardCheck },
    { href: "/faqs", label: "FAQs", icon: HelpCircle },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">MN</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-semibold text-navy text-lg">Storm Damage</span>
              <span className="text-gray-500 text-sm block -mt-1">Resource Center</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.href)
                    ? "text-orange"
                    : "text-gray-600 hover:text-navy"
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/#lead-form">
              <Button
                data-testid="header-cta-btn"
                className="bg-orange hover:bg-orange-hover text-white px-5 py-2.5 rounded-lg font-medium transition-all"
              >
                <Phone className="w-4 h-4 mr-2" />
                Get Connected
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-navy"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu with slide animation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen 
              ? "max-h-[500px] opacity-100" 
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="py-4 border-t border-gray-200 bg-white/95 backdrop-blur-xl">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link, index) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(link.href)
                      ? "bg-orange/10 text-orange"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={{ 
                    transitionDelay: mobileMenuOpen ? `${index * 50}ms` : "0ms",
                    transform: mobileMenuOpen ? "translateX(0)" : "translateX(-20px)",
                    opacity: mobileMenuOpen ? 1 : 0
                  }}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              <Link
                to="/reviews"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  location.pathname === '/reviews'
                    ? "bg-orange/10 text-orange"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                style={{ 
                  transitionDelay: mobileMenuOpen ? `${navLinks.length * 50}ms` : "0ms",
                  transform: mobileMenuOpen ? "translateX(0)" : "translateX(-20px)",
                  opacity: mobileMenuOpen ? 1 : 0
                }}
              >
                <MessageCircle className="w-5 h-5" />
                Reviews
              </Link>
              <div 
                className="mx-4 mt-3"
                style={{ 
                  transitionDelay: mobileMenuOpen ? `${(navLinks.length + 1) * 50}ms` : "0ms",
                  transform: mobileMenuOpen ? "translateY(0)" : "translateY(10px)",
                  opacity: mobileMenuOpen ? 1 : 0,
                  transition: "all 0.3s ease-in-out"
                }}
              >
                <Link
                  to="/#lead-form"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full bg-orange hover:bg-orange-hover text-white py-3 rounded-lg font-medium">
                    <Phone className="w-4 h-4 mr-2" />
                    Get Connected
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = ({ onCookieSettings }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">MN</span>
              </div>
              <div>
                <span className="font-heading font-semibold text-white text-lg">Minnesota Storm Damage</span>
                <span className="text-gray-400 text-sm block">Resource Center</span>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md">
              Helping Minnesota homeowners navigate storm damage with confidence. 
              Better information. Better decisions. Better contractor connections.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/service-areas", label: "Service Areas" },
                { to: "/roof-assessment", label: "Free Roof Quiz" },
                { to: "/reviews", label: "Reviews" },
                { to: "/resources", label: "Resources" },
                { to: "/faqs", label: "FAQs" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-orange transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Categories */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">We Help With</h4>
            <ul className="space-y-3">
              {[
                { to: "/storm-damage-restoration", label: "Storm Damage" },
                { to: "/roof-replacement", label: "Roof Replacement" },
                { to: "/insurance-claims", label: "Insurance Claims" },
                { to: "/hail-map", label: "Hail History Map" },
                { to: "/insurance-agents", label: "Find Insurance Agent" },
                { to: "/guides", label: "Free Guides" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-orange transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-navy-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} Minnesota Storm Damage Resource. All rights reserved.
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-orange text-sm transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <Link to="/insurance-agents" className="text-gray-500 hover:text-orange text-sm transition-colors">
              Find An Insurance Agent
            </Link>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <button 
              onClick={onCookieSettings}
              className="text-gray-500 hover:text-orange text-sm transition-colors flex items-center gap-1"
              data-testid="cookie-settings-link"
            >
              <Cookie className="w-3.5 h-3.5" />
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Cookie Consent Banner - GDPR Compliant
const CookieConsent = ({ forceShow = false, onClose }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (forceShow) {
      setShowBanner(true);
      setShowPreferences(true);
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        // Initialize analytics if previously accepted
        if (savedPrefs.analytics) {
          enableAnalytics();
        }
      } catch (e) {
        // Invalid stored data, show banner again
        setShowBanner(true);
      }
    }
  }, [forceShow]);

  const enableAnalytics = () => {
    // Enable Google Analytics tracking
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      });
    }
  };

  const disableAnalytics = () => {
    // Disable Google Analytics tracking
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
      });
    }
  };

  const handleAcceptAll = () => {
    const newPrefs = { necessary: true, analytics: true, marketing: true };
    setPreferences(newPrefs);
    localStorage.setItem("cookieConsent", JSON.stringify(newPrefs));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    enableAnalytics();
    setShowBanner(false);
    setShowPreferences(false);
    if (onClose) onClose();
  };

  const handleRejectAll = () => {
    const newPrefs = { necessary: true, analytics: false, marketing: false };
    setPreferences(newPrefs);
    localStorage.setItem("cookieConsent", JSON.stringify(newPrefs));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    disableAnalytics();
    setShowBanner(false);
    setShowPreferences(false);
    if (onClose) onClose();
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    if (preferences.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
    setShowBanner(false);
    setShowPreferences(false);
    if (onClose) onClose();
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-consent-banner"
    >
      {/* Main Banner */}
      {!showPreferences ? (
        <div className="bg-navy border-t border-gray-700 shadow-2xl">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Icon and Text */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 bg-orange/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Cookie className="w-5 h-5 text-orange" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white text-lg mb-1">
                    We Value Your Privacy
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, analyze site traffic, and understand where our visitors come from. 
                    By clicking &ldquo;Accept All&rdquo;, you consent to our use of cookies.{" "}
                    <button 
                      onClick={() => setShowPreferences(true)}
                      className="text-orange hover:underline"
                    >
                      Customize preferences
                    </button>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 w-full lg:w-auto">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white flex-1 lg:flex-none"
                  data-testid="cookie-reject-btn"
                >
                  Reject All
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-orange hover:bg-orange-hover text-white flex-1 lg:flex-none"
                  data-testid="cookie-accept-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Preferences Panel */
        <div className="bg-white border-t border-gray-200 shadow-2xl max-h-[80vh] overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-navy" />
                <h3 className="font-heading font-semibold text-navy text-xl">
                  Cookie Preferences
                </h3>
              </div>
              <button 
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Manage your cookie preferences below. You can enable or disable different types of cookies 
              and save your preferences. Note that blocking some cookies may impact your experience.
            </p>

            {/* Cookie Categories */}
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-navy">Necessary Cookies</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Always Active</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 ml-8">
                  Essential for the website to function properly. These cannot be disabled.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                      <span className="text-blue-600 text-xs font-bold">A</span>
                    </div>
                    <span className="font-medium text-navy">Analytics Cookies</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="sr-only peer"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-orange/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-500 ml-8">
                  Help us understand how visitors interact with our website by collecting anonymous data. 
                  We use Google Analytics to analyze traffic and improve our services.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-purple-100 rounded flex items-center justify-center">
                      <span className="text-purple-600 text-xs font-bold">M</span>
                    </div>
                    <span className="font-medium text-navy">Marketing Cookies</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                      className="sr-only peer"
                      data-testid="cookie-marketing-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-orange/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-500 ml-8">
                  Used to deliver personalized content and measure the effectiveness of our marketing campaigns.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-end border-t border-gray-200 pt-4">
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Reject All
              </Button>
              <Button
                onClick={handleSavePreferences}
                className="bg-orange hover:bg-orange-hover text-white"
                data-testid="cookie-save-btn"
              >
                Save Preferences
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-navy hover:bg-navy/90 text-white"
              >
                Accept All
              </Button>
            </div>

            {/* Privacy Note */}
            <p className="text-xs text-gray-400 mt-4 text-center">
              By using our site, you acknowledge that you have read and understood our cookie policy. 
              You can change your preferences at any time by clicking the cookie icon in the footer.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <div className="pt-20">
        <Breadcrumbs />
      </div>
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer onCookieSettings={() => setShowCookieSettings(true)} />
      <FloatingCTA />
      <CookieConsent key={showCookieSettings ? 'show' : 'hide'} forceShow={showCookieSettings} onClose={() => setShowCookieSettings(false)} />
    </div>
  );
}
